import React, { useEffect, useState } from 'react';
import { BotType } from '../../../utilities/type';
import { GetBotDetails } from '../../apis/get';
import AlertPopup from './AlertPopup';

interface FileUploadWithButtonProps {
  onFileUpload: (file: File, botType: BotType) => void;
  buttonLabel: string;
  fileTypes: string[];
  onStartBot: (botType: BotType, cities: string[]) => void;
  onBotTypeChange: (botType: BotType) => void;
}

const FileUploadWithButton: React.FC<FileUploadWithButtonProps> = ({
  onFileUpload,
  buttonLabel,
  fileTypes,
  onStartBot,
  onBotTypeChange,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [botType, setBotType] = useState<BotType>(BotType.DaliySalesReport);
  const [botCities, setBotCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const FetchBotCities = async (botType: string) => {
    const res = await GetBotDetails(botType);
    console.log(res);
    if (res.status) {
      setBotCities(res.cities);
      return true;
    }
    return false;
  };

  const handleCityToggle = (city: string) => {
    setSelectedCities(
      (prev) =>
        prev.includes(city)
          ? prev.filter((c) => c !== city) // remove if already selected
          : [...prev, city] // add if not selected
    );

    console.log("added: ", city);
    console.log("ad", selectedCities);
  };

  const handleBotTypeChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    let selectedType = e.target.value as BotType;
    
    // Skip market fetching for Predictive Scheduling & Taco Bell Delaget as they don't use standard market selection
    if (selectedType === BotType.PredictiveScheduling || selectedType === BotType.TacoBellDelaget) {
      setBotCities([]);
      setSelectedCities([]);
      setBotType(selectedType);
      onBotTypeChange(selectedType);
      return;
    }

    const res = await FetchBotCities(selectedType);
    if (!res) {
      return;
    }
    setSelectedCities([]);
    setBotType(selectedType);
    onBotTypeChange(selectedType);
  };
  const handleStartBot = () => {
    if (botType !== BotType.PredictiveScheduling && botType !== BotType.TacoBellDelaget && selectedCities.length === 0) {
      setAlertMessage("Please select at least one city.");
      return;
    }
    onStartBot(botType, selectedCities);
  };

  useEffect(() => {
    if (botType === BotType.PredictiveScheduling || botType === BotType.TacoBellDelaget) {
      setBotCities([]);
      return;
    }
    FetchBotCities(botType);
  }, [botType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = () => {
    if (file) {
      onFileUpload(file, botType);
    } else {
      alert("Please select a file before uploading.");
    }
  };

  return (
    <div className="bg-gray-800 p-8 rounded-2xl shadow-xl space-y-6 text-white">
      <div className="flex flex-col items-center justify-center space-y-4">
        {/* Hidden file input */}
        <input
          type="file"
          accept={fileTypes.join(",")}
          onChange={handleFileChange}
          id="file-upload"
          className="hidden"
        />

        {/* Custom Upload Button - Hidden for Selective Bots */}
        {botType !== BotType.PredictiveScheduling && botType !== BotType.TacoBellDelaget && (
          <label
            htmlFor="file-upload"
            className="cursor-pointer bg-yellow-500 text-black font-bold text-lg py-3 px-8 rounded-lg shadow-md hover:bg-yellow-600 transition duration-300 ease-in-out flex items-center justify-center w-full max-w-xs mx-auto"
          >
            <i className="fas fa-cloud-upload-alt mr-2"></i> {buttonLabel}
          </label>
        )}

        {/* File Name Display - Hidden for Selective Bots */}
        {botType !== BotType.PredictiveScheduling && botType !== BotType.TacoBellDelaget && (
          <div className="mt-4 text-yellow-300 text-sm font-medium">
            <span>
              <span className="font-semibold">
                {file?.name ? file.name : " No file selected."}
              </span>
            </span>
          </div>
        )}

        <div className="w-full max-w-xs mx-auto">
          {/* Bot Type Dropdown */}
          <label className="block text-yellow-300 text-sm font-medium mb-2">
            Bot Type
          </label>
          <select
            value={botType}
            onChange={handleBotTypeChange}
            className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value={BotType.DaliySalesReport}>Daily Sales Report</option>
            <option value={BotType.DiningRommSales}>Dining Room Sales</option>
            <option value={BotType.TacoBell}>Taco Bell</option>
            <option value={BotType.ActiveCrewStaff}>Active Crew Staff</option>
            <option value={BotType.PredictiveScheduling}>Predictive Scheduling</option>
            <option value={BotType.TacoBellDelaget}>Taco Bell Delaget Bot</option>
          </select>

          {/* Bot Cities - Hidden for Predictive Scheduling & Taco Bell Delaget */}
          {botType !== BotType.PredictiveScheduling && botType !== BotType.TacoBellDelaget && (
            <div className="mt-4 ">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-yellow-300 text-sm font-medium">
                  Select Markets
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedCities(
                      selectedCities.length === botCities.length
                        ? []
                        : [...botCities]
                    )
                  }
                  className="text-xs text-yellow-400 hover:text-yellow-300 underline"
                >
                  {selectedCities.length === botCities.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
              <div className="flex flex-wrap gap-3 max-w-[250px]">
                {botCities.map((city, index) => (
                  <label
                    key={city || index}
                    className="relative flex items-center cursor-pointer group"
                  >
                    {/* Hidden default checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(city)}
                      onChange={() => handleCityToggle(city)}
                      className="peer hidden"
                    />

                    {/* Custom futuristic box */}
                    <span
                      className="w-5 h-5 flex items-center justify-center rounded-md border border-yellow-500
                       peer-checked:bg-yellow-400 peer-checked:shadow-[0_0_10px_#FFD700]
                       transition duration-300 ease-in-out
                       group-hover:shadow-[0_0_6px_#FFD700]"
                    >
                      {/* Check icon */}
                      <svg
                        className="w-3 h-3 text-black opacity-0 peer-checked:opacity-100 transition"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>

                    {/* City name */}
                    <span className="ml-2 text-sm text-white group-hover:text-yellow-300 transition">
                      {city}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="flex justify-center gap-4 mt-4">
        {botType !== BotType.PredictiveScheduling && botType !== BotType.TacoBellDelaget && (
          <button
            type="button"
            onClick={handleUpload}
            className="bg-green-500 text-black font-bold px-6 py-3 rounded-lg shadow-md hover:bg-green-600 transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!file}
          >
            Upload
          </button>
        )}

        <button
          type="button"
          onClick={handleStartBot}
          className="bg-orange-500 text-black font-bold px-6 py-3 rounded-lg shadow-md hover:bg-orange-600 transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Start Bot
        </button>
      </div>
      {alertMessage && (
        <AlertPopup
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
};

export default FileUploadWithButton;
